/**
 * Helpers pour le contenu riche (HTML) des posts / blogs.
 * Le contenu est produit par l'éditeur Quill (RichTextEditor) et affiché
 * via RichTextContent. Les anciens contenus enregistrés en texte brut sont
 * convertis à la volée par `plainTextToHtml`.
 */

const FORBIDDEN_TAGS = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "form",
  "input",
  "textarea",
  "button",
];

const looksLikeHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(value);

const escapeHtml = (value = "") =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Retire les balises / attributs dangereux avant un dangerouslySetInnerHTML.
 */
export const sanitizeHtml = (html = "") => {
  if (typeof html !== "string" || !html.trim()) return "";
  if (typeof window === "undefined" || !window.DOMParser) return html;

  const doc = new window.DOMParser().parseFromString(html, "text/html");

  doc.body.querySelectorAll(FORBIDDEN_TAGS.join(",")).forEach((node) => node.remove());

  doc.body.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = (attr.value || "").trim().toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
      } else if (
        (name === "href" || name === "src" || name === "xlink:href") &&
        (value.startsWith("javascript:") || value.startsWith("data:text/html"))
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
};

/**
 * Version texte brut d'un contenu HTML (aperçus, partage, recherche).
 */
export const htmlToText = (html = "") => {
  if (typeof html !== "string" || !html) return "";
  if (!looksLikeHtml(html)) return html;
  if (typeof window === "undefined" || !window.DOMParser) {
    return html.replace(/<[^>]+>/g, " ");
  }
  const doc = new window.DOMParser().parseFromString(
    html.replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "$& "),
    "text/html"
  );
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

/**
 * `true` si le HTML ne contient ni texte ni média (Quill renvoie
 * "<p><br></p>" quand l'éditeur est vide).
 */
export const isHtmlEmpty = (html = "") => {
  if (!html || typeof html !== "string") return true;
  if (/<(img|video|audio)\b/i.test(html)) return false;
  return htmlToText(html).replace(/ /g, " ").trim().length === 0;
};

/**
 * Convertit un ancien contenu en texte brut vers du HTML (sauts de ligne
 * et paragraphes préservés). Un contenu déjà HTML est renvoyé tel quel.
 */
export const plainTextToHtml = (value = "") => {
  if (!value || typeof value !== "string") return "";
  if (looksLikeHtml(value)) return value;

  return value
    .split(/\r?\n\s*\r?\n/)
    .map((block) => `<p>${escapeHtml(block).replace(/\r?\n/g, "<br/>")}</p>`)
    .join("");
};
